import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

// ─── Input style constant ─────────────────────────────────────────
export const inputStyle = "rounded-xl border border-zinc-700 bg-black/40 text-zinc-100 placeholder:text-zinc-500 outline-none focus-visible:ring-0 px-4 py-2.5";

// ─── Sidebar navigation item ──────────────────────────────────────
export const SidebarItem = ({ icon, label, active, onClick, badge, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`flex items-center justify-between gap-3 w-full rounded-xl px-4 py-3 text-sm transition ${
      active
        ? "bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-white shadow-sm"
        : disabled
        ? "text-zinc-500 cursor-not-allowed"
        : "text-zinc-300 hover:bg-zinc-800/80 hover:text-zinc-100"
    }`}
  >
    <div className="flex items-center gap-3">
      {icon}
      {label}
    </div>
    {badge && (
      <span className="text-xs bg-violet-500/30 px-2 py-0.5 rounded text-violet-200">{badge}</span>
    )}
  </button>
);

// ─── Metric card for dashboard ────────────────────────────────────
export const MetricCard = ({ title, value, color }) => {
  const colors = {
    emerald: "text-emerald-400",
    violet: "text-violet-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };
  return (
    <Card className="bg-zinc-900 border border-zinc-800 rounded-2xl">
      <CardContent className="p-6">
        <p className="text-xs text-zinc-400 uppercase tracking-wide">{title}</p>
        <p className={`mt-2 text-3xl font-semibold ${colors[color] || 'text-white'}`}>{value}</p>
      </CardContent>
    </Card>
  );
};

// ─── Panel wrapper component ──────────────────────────────────────
export const DashboardPanel = ({ title, children, className = "", colSpan = "" }) => (
  <Card className={`bg-zinc-900 border border-zinc-800 rounded-2xl ${colSpan} ${className}`}>
    <CardContent className="p-6">
      {title && <h2 className="text-lg font-semibold mb-6 text-white">{title}</h2>}
      <div className="space-y-4">{children}</div>
    </CardContent>
  </Card>
);

// ─── Action pill for employee row actions ─────────────────────────
export const ActionPill = ({ label, tone, onClick, disabled }) => {
  const toneClass = {
    slate: "bg-zinc-700/70 hover:bg-zinc-600/70 text-zinc-100",
    indigo: "bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-200",
    violet: "bg-violet-500/15 hover:bg-violet-500/25 text-violet-200",
    rose: "bg-rose-500/15 hover:bg-rose-500/25 text-rose-200",
    emerald: "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-8 rounded-lg px-3 text-xs font-medium transition ${toneClass[tone] || toneClass.slate} border border-zinc-700/60 disabled:opacity-50`}
    >
      {label}
    </button>
  );
};

// ─── Avatar circle component ──────────────────────────────────────
export const AvatarCircle = ({ initials, className = "" }) => (
  <div className={`flex items-center justify-center rounded-2xl ${className}`}>
    <span className="text-sm font-semibold">{initials}</span>
  </div>
);

// ─── Get initials helper ──────────────────────────────────────────
export const getEmployeeInitials = (firstName, lastName) => {
  const first = firstName?.[0] || '';
  const last = lastName?.[0] || first;
  return (first + last).toUpperCase();
};

// ─── Employee hours row ───────────────────────────────────────────
export const EmployeeHoursRow = ({ name, hours }) => (
  <div className="flex items-center justify-between bg-zinc-800/60 rounded-xl px-5 py-4">
    <span className="text-sm text-white">{name}</span>
    <span className="text-sm text-zinc-300">{hours}</span>
  </div>
);

// ─── Legacy glass components (still used in some auth screens) ────
export const GlassButton = ({ children, className = "", ...props }) => (
  <motion.button
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.98 }}
    className={`px-4 py-2 rounded-2xl border border-zinc-700 bg-zinc-800/50 backdrop-blur-xl shadow-lg hover:bg-zinc-700/50 transition text-sm font-medium text-white ${className}`}
    {...props}
  >
    {children}
  </motion.button>
);

export const IOSGlassButton = ({ children, className = "", ...props }) => (
  <motion.button
    whileHover={{ scale: 1.06 }}
    whileTap={{ scale: 0.98 }}
    className={`px-4 py-2 rounded-2xl border border-zinc-600 bg-zinc-800/60 backdrop-blur-2xl shadow-lg hover:bg-zinc-700/60 transition text-sm font-semibold text-white ${className}`}
    {...props}
  >
    {children}
  </motion.button>
);

export const GlassCard = ({ className = "", children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className={`rounded-2xl border border-zinc-800 bg-zinc-900/80 backdrop-blur-2xl shadow-xl p-6 ${className}`}
  >
    {children}
  </motion.div>
);

export const GridTexture = ({ className = "" }) => (
  <div className={`pointer-events-none absolute inset-0 opacity-10 bg-[linear-gradient(0deg,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:12px_12px] ${className}`} />
);

// ─── Clock Logo ───────────────────────────────────────────────────
export const ClockLogo = ({ size = 36, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="tymeLogoGradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    <circle cx="50" cy="50" r="38" fill="none" stroke="url(#tymeLogoGradient)" strokeWidth="12" strokeLinecap="round" />
    <line x1="50" y1="50" x2="50" y2="28" stroke="url(#tymeLogoGradient)" strokeWidth="10" strokeLinecap="round" />
    <line x1="50" y1="50" x2="66" y2="66" stroke="url(#tymeLogoGradient)" strokeWidth="10" strokeLinecap="round" />
  </svg>
);
